import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { OverflowTooltip } from 'components/ui/OverflowTooltip';
import { getKeyboardNavigationIndex, isKeyboardActivationKey } from 'components/ui/utilities/keyboardNavigation';
import { PrismThemeProvider } from 'theme';
import type { TabNavigationProps } from './TabNavigation.props';
import {
  TabNavigationActions,
  TabNavigationItems,
  TabNavigationItemsArea,
  TabNavigationItemsViewport,
  TabNavigationRoot,
  TabNavigationScrollButton,
  TabNavigationTab,
  TabNavigationTabBadge,
  TabNavigationTabContent,
  TabNavigationTabLabel,
} from './TabNavigation.styles';
import { TAB_NAVIGATION_TEST_IDS } from './TabNavigation.testIds';

const getTextTooltipTitle = (label: TabNavigationProps['items'][number]['label']) =>
  typeof label === 'string' ? label : '';

const TabNavigation = ({
  actions,
  ariaLabel = 'Tabs',
  items,
  onChange,
  showScrollButtons = false,
  testId = TAB_NAVIGATION_TEST_IDS.root,
  value = false,
  zoomLevel = 1,
  ...props
}: TabNavigationProps) => {
  const itemsViewportRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    hasOverflow: false,
  });
  const setTabRef = useCallback(
    (itemValue: string) => (element: HTMLButtonElement | null) => {
      if (!element) {
        tabRefs.current.delete(itemValue);
        return;
      }

      tabRefs.current.set(itemValue, element);
    },
    []
  );
  const selectedIndex = items.findIndex(item => item.value === value);
  const shouldShowScrollButtons = showScrollButtons && items.length > 1 && scrollState.hasOverflow;

  const updateScrollState = useCallback(() => {
    const viewport = itemsViewportRef.current;

    if (!viewport) {
      return;
    }

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const nextScrollState = {
      canScrollLeft: viewport.scrollLeft > 1,
      canScrollRight: viewport.scrollLeft < maxScrollLeft - 1,
      hasOverflow: maxScrollLeft > 1,
    };

    setScrollState(currentScrollState =>
      currentScrollState.canScrollLeft === nextScrollState.canScrollLeft &&
      currentScrollState.canScrollRight === nextScrollState.canScrollRight &&
      currentScrollState.hasOverflow === nextScrollState.hasOverflow
        ? currentScrollState
        : nextScrollState
    );
  }, []);

  const requestScrollStateUpdate = useCallback(() => {
    if (typeof window === 'undefined') {
      updateScrollState();
      return;
    }

    window.requestAnimationFrame(updateScrollState);
  }, [updateScrollState]);

  const scrollTabIntoView = useCallback(
    (itemValue: string) => {
      tabRefs.current.get(itemValue)?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
      requestScrollStateUpdate();
    },
    [requestScrollStateUpdate]
  );

  const handleSelectTab = useCallback(
    (itemValue: string) => {
      onChange?.(itemValue);
      scrollTabIntoView(itemValue);
    },
    [onChange, scrollTabIntoView]
  );

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      const currentItem = items[currentIndex];
      if (isKeyboardActivationKey(event.key)) {
        event.preventDefault();
        if (!currentItem?.disabled) {
          handleSelectTab(currentItem.value);
        }
        return;
      }

      const nextIndex = getKeyboardNavigationIndex({
        currentIndex,
        itemCount: items.length,
        key: event.key,
        orientation: 'horizontal',
      });
      const nextItem = items[nextIndex];

      if (nextIndex < 0 || !nextItem || nextItem.disabled) {
        return;
      }

      event.preventDefault();
      tabRefs.current.get(nextItem.value)?.focus();
      handleSelectTab(nextItem.value);
    },
    [handleSelectTab, items]
  );

  const handleItemsScroll = useCallback(
    (direction: 'left' | 'right') => {
      const viewport = itemsViewportRef.current;

      if (!viewport) {
        return;
      }

      viewport.scrollBy({
        behavior: 'smooth',
        left: Math.max(160, viewport.clientWidth * 0.7) * (direction === 'left' ? -1 : 1),
      });
      requestScrollStateUpdate();
    },
    [requestScrollStateUpdate]
  );

  // Overflow arrows depend on rendered tab widths, so measure after layout and after resizes.
  useLayoutEffect(() => {
    const viewport = itemsViewportRef.current;

    if (!viewport) {
      return undefined;
    }

    updateScrollState();
    viewport.addEventListener('scroll', updateScrollState, { passive: true });

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateScrollState);
      return () => {
        viewport.removeEventListener('scroll', updateScrollState);
        window.removeEventListener('resize', updateScrollState);
      };
    }

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [items, showScrollButtons, updateScrollState]);

  // Keep controlled active tabs visible when the selected value changes outside this primitive.
  useEffect(() => {
    if (value === false) {
      return;
    }

    scrollTabIntoView(value);
  }, [scrollTabIntoView, value]);

  return (
    <PrismThemeProvider>
      <TabNavigationRoot aria-label={ariaLabel} data-testid={testId} zoomLevel={zoomLevel} {...props}>
        <TabNavigationItemsArea zoomLevel={zoomLevel}>
          {shouldShowScrollButtons && (
            <TabNavigationScrollButton
              aria-label="Scroll tabs left"
              data-testid={TAB_NAVIGATION_TEST_IDS.itemsScrollLeft}
              disabled={!scrollState.canScrollLeft}
              onClick={() => handleItemsScroll('left')}
              zoomLevel={zoomLevel}
              type="button">
              <ChevronLeftIcon fontSize="inherit" />
            </TabNavigationScrollButton>
          )}
          <TabNavigationItemsViewport
            ref={itemsViewportRef}
            data-testid={TAB_NAVIGATION_TEST_IDS.items}
            showScrollButtons={shouldShowScrollButtons}
            zoomLevel={zoomLevel}>
            <TabNavigationItems role="tablist" zoomLevel={zoomLevel}>
              {items.map((item, index) => {
                const isSelected = item.value === value;

                return (
                  <TabNavigationTab
                    key={item.value}
                    ref={setTabRef(item.value)}
                    aria-disabled={item.disabled || undefined}
                    aria-selected={isSelected}
                    data-testid={TAB_NAVIGATION_TEST_IDS.item(item.value)}
                    disabled={item.disabled}
                    isSelected={isSelected}
                    onClick={() => {
                      handleSelectTab(item.value);
                    }}
                    onKeyDown={event => handleTabKeyDown(event, index)}
                    role="tab"
                    tabIndex={isSelected || (selectedIndex < 0 && index === 0) ? 0 : -1}
                    zoomLevel={zoomLevel}
                    type="button">
                    <TabNavigationTabContent zoomLevel={zoomLevel}>
                      <OverflowTooltip title={getTextTooltipTitle(item.label)}>
                        <TabNavigationTabLabel>{item.label}</TabNavigationTabLabel>
                      </OverflowTooltip>
                      {item.badge != null ? (
                        <TabNavigationTabBadge zoomLevel={zoomLevel}>[{item.badge}]</TabNavigationTabBadge>
                      ) : null}
                    </TabNavigationTabContent>
                  </TabNavigationTab>
                );
              })}
            </TabNavigationItems>
          </TabNavigationItemsViewport>
          {shouldShowScrollButtons && (
            <TabNavigationScrollButton
              aria-label="Scroll tabs right"
              data-testid={TAB_NAVIGATION_TEST_IDS.itemsScrollRight}
              disabled={!scrollState.canScrollRight}
              onClick={() => handleItemsScroll('right')}
              zoomLevel={zoomLevel}
              type="button">
              <ChevronRightIcon fontSize="inherit" />
            </TabNavigationScrollButton>
          )}
        </TabNavigationItemsArea>
        {actions ? (
          <TabNavigationActions data-testid={TAB_NAVIGATION_TEST_IDS.actions}>{actions}</TabNavigationActions>
        ) : null}
      </TabNavigationRoot>
    </PrismThemeProvider>
  );
};

export default TabNavigation;
